import { createFileRoute, Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useStudentFocus } from '@/admin/studentFocus';
import type { BadgeVariant } from '@/admin/ui/Badge';
import { Badge } from '@/admin/ui/Badge';
import { StudentListHeader } from '@/admin/ui/ContentNav';
import { PageTitle } from '@/admin/ui/Page';
import { LIST_PAGE_SIZE, Pagination } from '@/admin/ui/Pagination';
import { Table } from '@/admin/ui/Table';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import i18n from '~/i18n';

type CertificateSummary = components['schemas']['CertificateSummary'];

type CertificatesSearch = {
  user_id?: number;
};

export const Route = createFileRoute('/admin/certificates/')({
  staticData: { minTier: 'ADMIN' },
  component: CertificatesList,
  validateSearch: (search: Record<string, unknown>): CertificatesSearch => {
    const raw = search.user_id;
    const id =
      typeof raw === 'number'
        ? raw
        : typeof raw === 'string' && raw !== ''
          ? Number(raw)
          : undefined;
    return {
      user_id: id !== undefined && Number.isFinite(id) ? id : undefined,
    };
  },
});

const columnHelper = createColumnHelper<CertificateSummary>();

function typeBadge(type: string): { label: string; variant: BadgeVariant } {
  return type === 'series'
    ? { label: i18n.t('admin.certificates.type.series'), variant: 'warning' }
    : { label: i18n.t('admin.certificates.type.course'), variant: 'neutral' };
}

const columns = [
  columnHelper.accessor('id', { header: () => i18n.t('admin.common.id') }),
  columnHelper.accessor('user_display_name', {
    header: () => i18n.t('admin.certificates.col.student'),
    cell: (info) => (
      <Link
        to="/admin/students/$studentId/"
        params={{ studentId: String(info.row.original.user_id) }}
        style={{ color: 'var(--blue)', textDecoration: 'none' }}
      >
        {info.getValue()}
      </Link>
    ),
  }),
  columnHelper.accessor('template_title', {
    header: () => i18n.t('admin.certificates.col.certificate'),
  }),
  columnHelper.accessor('type', {
    header: () => i18n.t('admin.certificates.col.type'),
    cell: (info) => {
      const { label, variant } = typeBadge(info.getValue());
      return <Badge $variant={variant}>{label}</Badge>;
    },
  }),
  columnHelper.display({
    id: 'status',
    header: () => i18n.t('admin.common.status'),
    cell: (info) =>
      info.row.original.is_pending ? (
        <Badge $variant="warning">
          {i18n.t('admin.certificates.status.pending')}
        </Badge>
      ) : (
        <Badge $variant="success">
          {i18n.t('admin.certificates.status.awarded')}
        </Badge>
      ),
  }),
  columnHelper.accessor('awarded_at', {
    header: () => i18n.t('admin.certificates.col.awarded'),
    cell: (info) => {
      const v = info.getValue();
      return v ? new Date(v).toLocaleString() : '—';
    },
  }),
];

function CertificatesList() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const [page, setPage] = useState(0);
  const { focusedStudent } = useStudentFocus();

  const effectiveUserId = search.user_id ?? focusedStudent?.id;

  const { data, isLoading, error } = api.listCertificates.useQuery({
    query: {
      limit: LIST_PAGE_SIZE,
      offset: LIST_PAGE_SIZE * page,
      ...(effectiveUserId !== undefined ? { user_id: effectiveUserId } : {}),
    },
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      {search.user_id !== undefined ? (
        <StudentListHeader
          studentId={search.user_id}
          title={t('admin.nav.certificates')}
        />
      ) : (
        <PageTitle>{t('admin.nav.certificates')}</PageTitle>
      )}

      {isLoading ? (
        <LoadingMessage>{t('admin.certificates.loading')}</LoadingMessage>
      ) : error ? (
        <ErrorMessage>{t('admin.certificates.errorLoading')}</ErrorMessage>
      ) : (
        <>
          <Pagination
            page={page}
            pageSize={LIST_PAGE_SIZE}
            shown={data?.data.length ?? 0}
            total={data?.count}
            onPage={setPage}
          />

          <Table table={table} />
          <Pagination
            page={page}
            pageSize={LIST_PAGE_SIZE}
            shown={data?.data.length ?? 0}
            total={data?.count}
            onPage={setPage}
            hideWhenAlone
          />
        </>
      )}
    </div>
  );
}

const LoadingMessage = styled.div`
  color: var(--grey-600);
  padding: 2rem;
  text-align: center;
`;

const ErrorMessage = styled.div`
  color: var(--error);
  padding: 2rem;
  text-align: center;
`;
